import { hasValue, toNullableNumber, toNullableText, pad } from '@/lib/helpers/numeric'
import useSQLite, { persistWebStore, readTableColumns } from '../useSQLite'
import { defineSqliteFeature } from './define-feature'

export const PHONE_GPS_RECORDING_STATUS_RECORDING = 'recording'
export const PHONE_GPS_RECORDING_STATUS_STOPPED = 'stopped'
export const PHONE_GPS_RECORDING_STATUS_INTERRUPTED = 'interrupted'

export const PHONE_GPS_RECORDING_SYNC_LOCAL = 'local'
export const PHONE_GPS_RECORDING_SYNC_UPLOADING = 'uploading'
export const PHONE_GPS_RECORDING_SYNC_SYNCED = 'synced'
export const PHONE_GPS_RECORDING_SYNC_ERROR = 'error'

export const PHONE_GPS_RECORDINGS_STORAGE_PATH = 'phone-gps-recordings'

const PHONE_GPS_RECORDING_STATUSES = new Set([
    PHONE_GPS_RECORDING_STATUS_RECORDING,
    PHONE_GPS_RECORDING_STATUS_STOPPED,
    PHONE_GPS_RECORDING_STATUS_INTERRUPTED,
])
const PHONE_GPS_RECORDING_SYNC_STATUSES = new Set([
    PHONE_GPS_RECORDING_SYNC_LOCAL,
    PHONE_GPS_RECORDING_SYNC_UPLOADING,
    PHONE_GPS_RECORDING_SYNC_SYNCED,
    PHONE_GPS_RECORDING_SYNC_ERROR,
])
const ABSOLUTE_TIMESTAMP_MS_THRESHOLD = 1_000_000_000_000
const DEFAULT_POINT_BATCH_SIZE = 75
const { dbRun, dbQuery, dbExecute } = useSQLite()

const SCHEMA_SQL = `
PRAGMA foreign_keys = ON;
CREATE TABLE IF NOT EXISTS phone_gps_sessions (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    recording_uuid TEXT NOT NULL UNIQUE,
    filename TEXT NOT NULL UNIQUE,
    user_id INTEGER NOT NULL,
    status TEXT NOT NULL DEFAULT 'stopped',
    sync_status TEXT NOT NULL DEFAULT 'local',
    started_at INTEGER NOT NULL,
    ended_at INTEGER,
    horse_id INTEGER,
    rider_id INTEGER,
    discipline TEXT,
    use_polar INTEGER NOT NULL DEFAULT 0,
    polar_device_id TEXT,
    polar_device_name TEXT,
    polar_expected_exercise_ts INTEGER,
    point_count INTEGER NOT NULL DEFAULT 0,
    native_manifest_path TEXT,
    local_file_path TEXT NOT NULL,
    checksum_sha256 TEXT,
    server_session_id INTEGER,
    server_upload_id INTEGER,
    synced_at INTEGER,
    last_lookup_at INTEGER,
    upload_attempt_count INTEGER NOT NULL DEFAULT 0,
    last_error_code TEXT,
    last_error_message TEXT,
    program_guidance TEXT,
    declared_gaits TEXT,
    declared_gaits_applied_at INTEGER,
    declared_gaits_error TEXT,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
);
CREATE TABLE IF NOT EXISTS phone_gps_points (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    recording_uuid TEXT NOT NULL,
    seq INTEGER NOT NULL,
    timestamp INTEGER NOT NULL,
    latitude REAL NOT NULL,
    longitude REAL NOT NULL,
    accuracy REAL,
    altitude REAL,
    altitude_accuracy REAL,
    speed REAL,
    heading REAL,
    inserted_at INTEGER NOT NULL,
    UNIQUE(recording_uuid, seq)
);
CREATE INDEX IF NOT EXISTS idx_phone_gps_sessions_user_sync
    ON phone_gps_sessions(user_id, sync_status);
CREATE INDEX IF NOT EXISTS idx_phone_gps_sessions_filename
    ON phone_gps_sessions(filename);
CREATE INDEX IF NOT EXISTS idx_phone_gps_points_recording_uuid
    ON phone_gps_points(recording_uuid);
`

const REQUIRED_COLUMNS = [
    {
        name: 'use_polar',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN use_polar INTEGER NOT NULL DEFAULT 0;',
    },
    {
        name: 'polar_device_id',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN polar_device_id TEXT;',
    },
    {
        name: 'polar_device_name',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN polar_device_name TEXT;',
    },
    {
        name: 'polar_expected_exercise_ts',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN polar_expected_exercise_ts INTEGER;',
    },
    // The program day snapshotted when the ride starts (JSON), and the gaits its
    // rider declared at the end (JSON), sent to the server session once synced.
    {
        name: 'program_guidance',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN program_guidance TEXT;',
    },
    {
        name: 'declared_gaits',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN declared_gaits TEXT;',
    },
    {
        name: 'declared_gaits_applied_at',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN declared_gaits_applied_at INTEGER;',
    },
    {
        name: 'declared_gaits_error',
        sql: 'ALTER TABLE phone_gps_sessions ADD COLUMN declared_gaits_error TEXT;',
    },
]

let schemaReady = false
let schemaPromise = null

// JSON columns: stored as text, read back as objects (null when empty or unreadable).
const toNullableJson = (value) => {
    if (!hasValue(value)) return null
    if (typeof value === 'string') return toNullableText(value)
    try {
        return JSON.stringify(value)
    } catch {
        return null
    }
}

const parseNullableJson = (value) => {
    const text = toNullableText(value)
    if (!text) return null
    try {
        const parsed = JSON.parse(text)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch {
        return null
    }
}

const toRequiredNumber = (value, label) => {
    if (!hasValue(value)) {
        throw new Error(`${label} is required`)
    }

    const number = Number(value)
    if (!Number.isFinite(number)) {
        throw new Error(`${label} is required`)
    }
    return number
}

export const normalizePhoneGpsTimestampMs = (value, fallback = null) => {
    const input = hasValue(value) ? value : fallback
    if (input instanceof Date) {
        const timestamp = input.getTime()
        return Number.isFinite(timestamp) ? timestamp : null
    }

    const number = Number(input)
    if (Number.isFinite(number) && number > 0) {
        return Math.round(number < ABSOLUTE_TIMESTAMP_MS_THRESHOLD ? number * 1000 : number)
    }

    if (typeof input === 'string') {
        const parsed = Date.parse(input)
        return Number.isFinite(parsed) ? parsed : null
    }

    return null
}

const normalizePhoneGpsRecordingStatus = (status, fallback = PHONE_GPS_RECORDING_STATUS_STOPPED) => {
    const normalized = toNullableText(status)
    return PHONE_GPS_RECORDING_STATUSES.has(normalized) ? normalized : fallback
}

const normalizePhoneGpsRecordingSyncStatus = (syncStatus, fallback = PHONE_GPS_RECORDING_SYNC_LOCAL) => {
    const normalized = toNullableText(syncStatus)
    return PHONE_GPS_RECORDING_SYNC_STATUSES.has(normalized) ? normalized : fallback
}

export const generateRecordingUuid = () => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID()
    }

    const timePart = Date.now().toString(36)
    const randomPart = Math.random().toString(36).slice(2, 10)
    return `${timePart}-${randomPart}`
}

const sanitizeRecordingUuidPrefix = (recordingUuid) => {
    const normalized = toNullableText(recordingUuid) || generateRecordingUuid()
    const compact = normalized
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 8)
        .toLowerCase()
    return compact || 'recordng'
}

const formatFilenameTimestamp = (timestampMs) => {
    const date = new Date(timestampMs)
    const datePart = [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join('')
    const timePart = [pad(date.getHours()), pad(date.getMinutes())].join('')
    return `${datePart}_${timePart}`
}

export const buildPhoneGpsRecordingFilename = ({ recordingUuid, startedAt = Date.now() } = {}) => {
    const timestamp = normalizePhoneGpsTimestampMs(startedAt, Date.now()) || Date.now()
    return `phone_${sanitizeRecordingUuidPrefix(recordingUuid)}_${formatFilenameTimestamp(timestamp)}.json`
}

export const buildPhoneGpsRecordingLocalFilePath = (filename) =>
    `${PHONE_GPS_RECORDINGS_STORAGE_PATH}/${String(filename || '').trim()}`

const mapPhoneGpsSessionRow = (row) => {
    if (!row) return null

    return {
        id: Number(row.id),
        recordingUuid: row.recording_uuid,
        filename: row.filename,
        userId: Number(row.user_id),
        status: normalizePhoneGpsRecordingStatus(row.status),
        syncStatus: normalizePhoneGpsRecordingSyncStatus(row.sync_status),
        startedAt: normalizePhoneGpsTimestampMs(row.started_at),
        endedAt: normalizePhoneGpsTimestampMs(row.ended_at),
        horseId: toNullableNumber(row.horse_id),
        riderId: toNullableNumber(row.rider_id),
        discipline: toNullableText(row.discipline),
        usePolar: Number(row.use_polar) === 1,
        polarDeviceId: toNullableText(row.polar_device_id),
        polarDeviceName: toNullableText(row.polar_device_name),
        polarExpectedExerciseTs: normalizePhoneGpsTimestampMs(row.polar_expected_exercise_ts),
        pointCount: Number(row.point_count) || 0,
        nativeManifestPath: toNullableText(row.native_manifest_path),
        localFilePath: row.local_file_path,
        checksumSha256: toNullableText(row.checksum_sha256),
        serverSessionId: toNullableNumber(row.server_session_id),
        serverUploadId: toNullableNumber(row.server_upload_id),
        syncedAt: normalizePhoneGpsTimestampMs(row.synced_at),
        lastLookupAt: normalizePhoneGpsTimestampMs(row.last_lookup_at),
        uploadAttemptCount: Number(row.upload_attempt_count) || 0,
        lastErrorCode: toNullableText(row.last_error_code),
        lastErrorMessage: toNullableText(row.last_error_message),
        programGuidance: parseNullableJson(row.program_guidance),
        declaredGaits: parseNullableJson(row.declared_gaits),
        declaredGaitsAppliedAt: normalizePhoneGpsTimestampMs(row.declared_gaits_applied_at),
        declaredGaitsError: toNullableText(row.declared_gaits_error),
        createdAt: normalizePhoneGpsTimestampMs(row.created_at),
        updatedAt: normalizePhoneGpsTimestampMs(row.updated_at),
    }
}

const mapPhoneGpsPointRow = (row) => {
    if (!row) return null

    return {
        id: Number(row.id) || null,
        recordingUuid: row.recording_uuid,
        seq: Number(row.seq),
        timestamp: normalizePhoneGpsTimestampMs(row.timestamp),
        latitude: Number(row.latitude),
        longitude: Number(row.longitude),
        accuracy: toNullableNumber(row.accuracy),
        altitude: toNullableNumber(row.altitude),
        altitudeAccuracy: toNullableNumber(row.altitude_accuracy),
        speed: toNullableNumber(row.speed),
        heading: toNullableNumber(row.heading),
        insertedAt: normalizePhoneGpsTimestampMs(row.inserted_at),
    }
}

const normalizePhoneGpsPoint = (point, index = 0) => {
    const coords = point?.coords || {}
    const seq = Number(point?.seq ?? point?.sequence ?? index + 1)
    const timestamp = normalizePhoneGpsTimestampMs(point?.timestamp ?? point?.t ?? point?.time)
    const latitude = Number(point?.latitude ?? point?.lat ?? coords.latitude)
    const longitude = Number(point?.longitude ?? point?.lon ?? point?.lng ?? coords.longitude)

    if (!Number.isFinite(seq) || seq <= 0) return null
    if (!timestamp) return null
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

    return {
        seq: Math.round(seq),
        timestamp,
        latitude,
        longitude,
        accuracy: toNullableNumber(point?.accuracy ?? coords.accuracy),
        altitude: toNullableNumber(point?.altitude ?? point?.alt ?? coords.altitude),
        altitudeAccuracy: toNullableNumber(
            point?.altitudeAccuracy ?? point?.altitude_accuracy ?? coords.altitudeAccuracy
        ),
        speed: toNullableNumber(point?.speed ?? coords.speed),
        heading: toNullableNumber(point?.heading ?? coords.heading),
    }
}

const ensurePhoneGpsRecordingColumns = async () => {
    const existingColumns = await readTableColumns('phone_gps_sessions')
    for (const column of REQUIRED_COLUMNS) {
        if (existingColumns.has(column.name)) continue
        await dbExecute(column.sql)
    }
}

const readPhoneGpsSessionByWhereClause = async (whereClause, params = []) => {
    await ensurePhoneGpsRecordingSchema()
    const result = await dbQuery(
        `SELECT
            id,
            recording_uuid,
            filename,
            user_id,
            status,
            sync_status,
            started_at,
            ended_at,
            horse_id,
            rider_id,
            discipline,
            use_polar,
            polar_device_id,
            polar_device_name,
            polar_expected_exercise_ts,
            point_count,
            native_manifest_path,
            local_file_path,
            checksum_sha256,
            server_session_id,
            server_upload_id,
            synced_at,
            last_lookup_at,
            upload_attempt_count,
            last_error_code,
            last_error_message,
            program_guidance,
            declared_gaits,
            declared_gaits_applied_at,
            declared_gaits_error,
            created_at,
            updated_at
         FROM phone_gps_sessions
         WHERE ${whereClause}
         LIMIT 1;`,
        params
    )

    return mapPhoneGpsSessionRow(result?.values?.[0])
}

const updatePhoneGpsSessionByUuid = async (recordingUuid, assignments, params) => {
    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) {
        throw new Error('recordingUuid is required')
    }
    if (!assignments.length) {
        return getPhoneGpsRecordingSessionByUuid(normalizedRecordingUuid)
    }

    await ensurePhoneGpsRecordingSchema()
    await dbRun(
        `UPDATE phone_gps_sessions
         SET ${assignments.join(', ')}, updated_at = ?
         WHERE recording_uuid = ?;`,
        [...params, Date.now(), normalizedRecordingUuid]
    )
    await persistWebStore()
    return getPhoneGpsRecordingSessionByUuid(normalizedRecordingUuid)
}

const persistIfChanged = async (changedCount) => {
    if (changedCount > 0) {
        await persistWebStore()
    }
}

const getResultChanges = (result) => Number(result?.changes?.changes) || 0

export const ensurePhoneGpsRecordingSchema = async () => {
    if (schemaReady) return
    if (schemaPromise) return schemaPromise

    schemaPromise = dbExecute(SCHEMA_SQL)
        .then(() => ensurePhoneGpsRecordingColumns())
        .then(() => {
            schemaReady = true
        })
        .finally(() => {
            schemaPromise = null
        })

    return schemaPromise
}

export const getPhoneGpsRecordingSessionByUuid = async (recordingUuid) => {
    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) return null
    return readPhoneGpsSessionByWhereClause('recording_uuid = ?', [normalizedRecordingUuid])
}

export const getPhoneGpsRecordingSessionByFilename = async (filename) => {
    const normalizedFilename = toNullableText(filename)
    if (!normalizedFilename) return null
    return readPhoneGpsSessionByWhereClause('filename = ?', [normalizedFilename])
}

export const createPhoneGpsRecordingSession = async ({
    recordingUuid = null,
    userId,
    status = PHONE_GPS_RECORDING_STATUS_RECORDING,
    syncStatus = PHONE_GPS_RECORDING_SYNC_LOCAL,
    startedAt = Date.now(),
    endedAt = null,
    horseId = null,
    riderId = null,
    discipline = null,
    usePolar = false,
    polarDeviceId = null,
    polarDeviceName = null,
    polarExpectedExerciseTs = null,
    pointCount = 0,
    nativeManifestPath = null,
    localFilePath = null,
    filename = null,
    checksumSha256 = null,
    programGuidance = null,
} = {}) => {
    await ensurePhoneGpsRecordingSchema()

    const now = Date.now()
    const normalizedRecordingUuid = toNullableText(recordingUuid) || generateRecordingUuid()
    const existingSession = await getPhoneGpsRecordingSessionByUuid(normalizedRecordingUuid)
    if (existingSession) return existingSession

    const normalizedStartedAt = normalizePhoneGpsTimestampMs(startedAt, now) || now
    const normalizedFilename =
        toNullableText(filename) ||
        buildPhoneGpsRecordingFilename({
            recordingUuid: normalizedRecordingUuid,
            startedAt: normalizedStartedAt,
        })
    const normalizedLocalFilePath =
        toNullableText(localFilePath) || buildPhoneGpsRecordingLocalFilePath(normalizedFilename)

    await dbRun(
        `INSERT INTO phone_gps_sessions (
            recording_uuid,
            filename,
            user_id,
            status,
            sync_status,
            started_at,
            ended_at,
            horse_id,
            rider_id,
            discipline,
            use_polar,
            polar_device_id,
            polar_device_name,
            polar_expected_exercise_ts,
            point_count,
            native_manifest_path,
            local_file_path,
            checksum_sha256,
            program_guidance,
            created_at,
            updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`,
        [
            normalizedRecordingUuid,
            normalizedFilename,
            toRequiredNumber(userId, 'userId'),
            normalizePhoneGpsRecordingStatus(status, PHONE_GPS_RECORDING_STATUS_RECORDING),
            normalizePhoneGpsRecordingSyncStatus(syncStatus),
            normalizedStartedAt,
            normalizePhoneGpsTimestampMs(endedAt),
            toNullableNumber(horseId),
            toNullableNumber(riderId),
            toNullableText(discipline),
            usePolar === true ? 1 : 0,
            toNullableText(polarDeviceId),
            toNullableText(polarDeviceName),
            normalizePhoneGpsTimestampMs(polarExpectedExerciseTs),
            Math.max(0, Number(pointCount) || 0),
            toNullableText(nativeManifestPath),
            normalizedLocalFilePath,
            toNullableText(checksumSha256),
            toNullableJson(programGuidance),
            now,
            now,
        ]
    )

    await persistWebStore()
    return getPhoneGpsRecordingSessionByUuid(normalizedRecordingUuid)
}

export const completePhoneGpsRecordingSession = async (
    recordingUuid,
    {
        endedAt = Date.now(),
        pointCount = undefined,
        nativeManifestPath = undefined,
        localFilePath = undefined,
        checksumSha256 = undefined,
        status = PHONE_GPS_RECORDING_STATUS_STOPPED,
    } = {}
) => {
    const assignments = ['status = ?', 'ended_at = ?']
    const params = [
        normalizePhoneGpsRecordingStatus(status, PHONE_GPS_RECORDING_STATUS_STOPPED),
        normalizePhoneGpsTimestampMs(endedAt, Date.now()),
    ]

    if (pointCount !== undefined) {
        assignments.push('point_count = ?')
        params.push(Math.max(0, Number(pointCount) || 0))
    }
    if (nativeManifestPath !== undefined) {
        assignments.push('native_manifest_path = ?')
        params.push(toNullableText(nativeManifestPath))
    }
    if (localFilePath !== undefined) {
        const normalizedLocalFilePath = toNullableText(localFilePath)
        if (normalizedLocalFilePath) {
            assignments.push('local_file_path = ?')
            params.push(normalizedLocalFilePath)
        }
    }
    if (checksumSha256 !== undefined) {
        assignments.push('checksum_sha256 = ?')
        params.push(toNullableText(checksumSha256))
    }

    return updatePhoneGpsSessionByUuid(recordingUuid, assignments, params)
}

export const markPhoneGpsRecordingInterrupted = async (
    recordingUuid,
    {
        endedAt = Date.now(),
        pointCount = undefined,
        nativeManifestPath = undefined,
        localFilePath = undefined,
        code = 'recording_interrupted',
        message = null,
    } = {}
) => {
    const assignments = ['status = ?', 'ended_at = ?', 'last_error_code = ?', 'last_error_message = ?']
    const params = [
        PHONE_GPS_RECORDING_STATUS_INTERRUPTED,
        normalizePhoneGpsTimestampMs(endedAt, Date.now()),
        toNullableText(code) || 'recording_interrupted',
        toNullableText(message),
    ]

    if (pointCount !== undefined) {
        assignments.push('point_count = ?')
        params.push(Math.max(0, Number(pointCount) || 0))
    }
    if (nativeManifestPath !== undefined) {
        assignments.push('native_manifest_path = ?')
        params.push(toNullableText(nativeManifestPath))
    }
    if (localFilePath !== undefined) {
        const normalizedLocalFilePath = toNullableText(localFilePath)
        if (normalizedLocalFilePath) {
            assignments.push('local_file_path = ?')
            params.push(normalizedLocalFilePath)
        }
    }

    return updatePhoneGpsSessionByUuid(recordingUuid, assignments, params)
}

export const updatePhoneGpsRecordingChecksum = async (recordingUuid, checksumSha256) =>
    updatePhoneGpsSessionByUuid(recordingUuid, ['checksum_sha256 = ?'], [toNullableText(checksumSha256)])

export const updatePhoneGpsRecordingPolarMetadata = async (
    recordingUuid,
    { usePolar = false, deviceId = null, deviceName = null, expectedExerciseTs = null } = {}
) =>
    updatePhoneGpsSessionByUuid(
        recordingUuid,
        ['use_polar = ?', 'polar_device_id = ?', 'polar_device_name = ?', 'polar_expected_exercise_ts = ?'],
        [
            usePolar === true ? 1 : 0,
            toNullableText(deviceId),
            toNullableText(deviceName),
            normalizePhoneGpsTimestampMs(expectedExerciseTs),
        ]
    )

// The program snapshot with what the rider tapped during the guided ride
// ("+1 min", "Next gait"), so a WebView reload restores the guidance.
export const updatePhoneGpsRecordingProgramGuidance = async (recordingUuid, programGuidance) =>
    updatePhoneGpsSessionByUuid(recordingUuid, ['program_guidance = ?'], [toNullableJson(programGuidance)])

// Saving the gaits (again) makes them pending: they are sent at the next synchronisation.
export const updatePhoneGpsRecordingDeclaredGaits = async (recordingUuid, declaredGaits) =>
    updatePhoneGpsSessionByUuid(
        recordingUuid,
        ['declared_gaits = ?', 'declared_gaits_applied_at = NULL', 'declared_gaits_error = NULL'],
        [toNullableJson(declaredGaits)]
    )

export const markPhoneGpsRecordingDeclaredGaitsApplied = async (recordingUuid, appliedAt = Date.now()) =>
    updatePhoneGpsSessionByUuid(
        recordingUuid,
        ['declared_gaits_applied_at = ?', 'declared_gaits_error = NULL'],
        [normalizePhoneGpsTimestampMs(appliedAt, Date.now())]
    )

// Refused for good (403/404): kept with the reason, never retried.
export const markPhoneGpsRecordingDeclaredGaitsFailed = async (recordingUuid, code = 'apply_failed') =>
    updatePhoneGpsSessionByUuid(recordingUuid, ['declared_gaits_error = ?'], [toNullableText(code) || 'apply_failed'])

export const markPhoneGpsRecordingUploading = async (recordingUuid, { checksumSha256 = undefined } = {}) => {
    const assignments = [
        'sync_status = ?',
        'upload_attempt_count = upload_attempt_count + 1',
        'last_error_code = NULL',
        'last_error_message = NULL',
    ]
    const params = [PHONE_GPS_RECORDING_SYNC_UPLOADING]

    if (checksumSha256 !== undefined) {
        assignments.push('checksum_sha256 = ?')
        params.push(toNullableText(checksumSha256))
    }

    return updatePhoneGpsSessionByUuid(recordingUuid, assignments, params)
}

export const markPhoneGpsRecordingUploadError = async (
    recordingUuid,
    { code = 'upload_failed', message = null } = {}
) =>
    updatePhoneGpsSessionByUuid(
        recordingUuid,
        ['sync_status = ?', 'last_error_code = ?', 'last_error_message = ?'],
        [PHONE_GPS_RECORDING_SYNC_ERROR, toNullableText(code) || 'upload_failed', toNullableText(message)]
    )

export const markPhoneGpsRecordingSynced = async (
    recordingUuid,
    {
        serverSessionId = undefined,
        serverUploadId = undefined,
        checksumSha256 = undefined,
        syncedAt = Date.now(),
        lastLookupAt = undefined,
    } = {}
) => {
    const assignments = ['sync_status = ?', 'synced_at = ?', 'last_error_code = NULL', 'last_error_message = NULL']
    const params = [PHONE_GPS_RECORDING_SYNC_SYNCED, normalizePhoneGpsTimestampMs(syncedAt, Date.now())]

    if (serverSessionId !== undefined) {
        assignments.push('server_session_id = ?')
        params.push(toNullableNumber(serverSessionId))
    }
    if (serverUploadId !== undefined) {
        assignments.push('server_upload_id = ?')
        params.push(toNullableNumber(serverUploadId))
    }
    if (checksumSha256 !== undefined) {
        assignments.push('checksum_sha256 = ?')
        params.push(toNullableText(checksumSha256))
    }
    if (lastLookupAt !== undefined) {
        assignments.push('last_lookup_at = ?')
        params.push(normalizePhoneGpsTimestampMs(lastLookupAt, Date.now()))
    }

    return updatePhoneGpsSessionByUuid(recordingUuid, assignments, params)
}

export const markPhoneGpsRecordingLookupChecked = async (
    recordingUuid,
    { lastLookupAt = Date.now(), errorCode = undefined, errorMessage = undefined } = {}
) => {
    const assignments = ['last_lookup_at = ?']
    const params = [normalizePhoneGpsTimestampMs(lastLookupAt, Date.now())]

    if (errorCode !== undefined) {
        assignments.push('last_error_code = ?')
        params.push(toNullableText(errorCode))
    }
    if (errorMessage !== undefined) {
        assignments.push('last_error_message = ?')
        params.push(toNullableText(errorMessage))
    }

    return updatePhoneGpsSessionByUuid(recordingUuid, assignments, params)
}

export const applyPhoneGpsRecordingUploadLookup = async (recordingUuid, lookup = {}) => {
    const now = Date.now()
    if (lookup?.status === 'success' && lookup?.exists === true) {
        const upload = Array.isArray(lookup.uploads) ? lookup.uploads[0] : null
        return markPhoneGpsRecordingSynced(recordingUuid, {
            serverSessionId: upload?.session_id,
            serverUploadId: upload?.upload_id,
            syncedAt: now,
            lastLookupAt: now,
        })
    }

    return markPhoneGpsRecordingLookupChecked(recordingUuid, {
        lastLookupAt: now,
        errorCode: lookup?.status === 'error' ? 'lookup_failed' : undefined,
        errorMessage: lookup?.error,
    })
}

export const insertPhoneGpsRecordingPoints = async (
    recordingUuid,
    points = [],
    { clearExisting = false, batchSize = DEFAULT_POINT_BATCH_SIZE } = {}
) => {
    await ensurePhoneGpsRecordingSchema()

    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) {
        throw new Error('recordingUuid is required')
    }

    const normalizedPoints = (Array.isArray(points) ? points : [])
        .map((point, index) => normalizePhoneGpsPoint(point, index))
        .filter(Boolean)

    if (clearExisting) {
        await dbRun('DELETE FROM phone_gps_points WHERE recording_uuid = ?;', [normalizedRecordingUuid])
    }

    const normalizedBatchSize = Math.max(
        1,
        Math.min(DEFAULT_POINT_BATCH_SIZE, Number(batchSize) || DEFAULT_POINT_BATCH_SIZE)
    )
    const insertedAt = Date.now()

    for (let index = 0; index < normalizedPoints.length; index += normalizedBatchSize) {
        const batch = normalizedPoints.slice(index, index + normalizedBatchSize)
        const placeholders = batch.map(() => '(?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)').join(', ')
        const params = batch.flatMap((point) => [
            normalizedRecordingUuid,
            point.seq,
            point.timestamp,
            point.latitude,
            point.longitude,
            point.accuracy,
            point.altitude,
            point.altitudeAccuracy,
            point.speed,
            point.heading,
            insertedAt,
        ])

        await dbRun(
            `INSERT OR REPLACE INTO phone_gps_points (
                recording_uuid,
                seq,
                timestamp,
                latitude,
                longitude,
                accuracy,
                altitude,
                altitude_accuracy,
                speed,
                heading,
                inserted_at
            ) VALUES ${placeholders};`,
            params
        )
    }

    const pointCount = await refreshPhoneGpsRecordingPointCount(normalizedRecordingUuid)
    return { insertedCount: normalizedPoints.length, pointCount }
}

export const refreshPhoneGpsRecordingPointCount = async (recordingUuid) => {
    await ensurePhoneGpsRecordingSchema()

    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) {
        throw new Error('recordingUuid is required')
    }

    const result = await dbQuery('SELECT COUNT(*) as count FROM phone_gps_points WHERE recording_uuid = ?;', [
        normalizedRecordingUuid,
    ])
    const pointCount = Number(result?.values?.[0]?.count) || 0
    await dbRun(
        `UPDATE phone_gps_sessions
         SET point_count = ?, updated_at = ?
         WHERE recording_uuid = ?;`,
        [pointCount, Date.now(), normalizedRecordingUuid]
    )
    await persistWebStore()
    return pointCount
}

export const fetchPhoneGpsRecordingPoints = async (
    recordingUuid,
    { limit = 1000, offset = 0, withId = false } = {}
) => {
    await ensurePhoneGpsRecordingSchema()

    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) return []

    const maxRows = Math.max(1, Number(limit) || 1000)
    const rowOffset = Math.max(0, Number(offset) || 0)
    const idColumn = withId ? 'id,' : ''
    const result = await dbQuery(
        `SELECT
            ${idColumn}
            recording_uuid,
            seq,
            timestamp,
            latitude,
            longitude,
            accuracy,
            altitude,
            altitude_accuracy,
            speed,
            heading,
            inserted_at
         FROM phone_gps_points
         WHERE recording_uuid = ?
         ORDER BY seq ASC
         LIMIT ? OFFSET ?;`,
        [normalizedRecordingUuid, maxRows, rowOffset]
    )

    return (result?.values ?? []).map(mapPhoneGpsPointRow).filter(Boolean)
}

export const listPhoneGpsRecordingSessions = async ({
    userId = null,
    syncStatus = null,
    limit = 50,
    offset = 0,
} = {}) => {
    await ensurePhoneGpsRecordingSchema()

    const where = []
    const params = []
    const normalizedUserId = toNullableNumber(userId)
    const normalizedSyncStatus = normalizePhoneGpsRecordingSyncStatus(syncStatus, null)

    if (normalizedUserId !== null) {
        where.push('user_id = ?')
        params.push(normalizedUserId)
    }
    if (normalizedSyncStatus) {
        where.push('sync_status = ?')
        params.push(normalizedSyncStatus)
    }

    const whereClause = where.length ? `WHERE ${where.join(' AND ')}` : ''
    const result = await dbQuery(
        `SELECT
            id,
            recording_uuid,
            filename,
            user_id,
            status,
            sync_status,
            started_at,
            ended_at,
            horse_id,
            rider_id,
            discipline,
            use_polar,
            polar_device_id,
            polar_device_name,
            polar_expected_exercise_ts,
            point_count,
            native_manifest_path,
            local_file_path,
            checksum_sha256,
            server_session_id,
            server_upload_id,
            synced_at,
            last_lookup_at,
            upload_attempt_count,
            last_error_code,
            last_error_message,
            program_guidance,
            declared_gaits,
            declared_gaits_applied_at,
            declared_gaits_error,
            created_at,
            updated_at
         FROM phone_gps_sessions
         ${whereClause}
         ORDER BY started_at DESC
         LIMIT ? OFFSET ?;`,
        [...params, Math.max(1, Number(limit) || 50), Math.max(0, Number(offset) || 0)]
    )

    return (result?.values ?? []).map(mapPhoneGpsSessionRow).filter(Boolean)
}

export const removePhoneGpsRecordingSession = async (recordingUuid) => {
    await ensurePhoneGpsRecordingSchema()

    const normalizedRecordingUuid = toNullableText(recordingUuid)
    if (!normalizedRecordingUuid) {
        throw new Error('recordingUuid is required')
    }

    await dbRun('DELETE FROM phone_gps_points WHERE recording_uuid = ?;', [normalizedRecordingUuid])
    const result = await dbRun('DELETE FROM phone_gps_sessions WHERE recording_uuid = ?;', [normalizedRecordingUuid])
    const removedCount = getResultChanges(result)
    await persistIfChanged(removedCount)
    return { ok: true, removedCount }
}

export const recoverInterruptedPhoneGpsUploads = async () => {
    await ensurePhoneGpsRecordingSchema()

    const result = await dbRun(
        `UPDATE phone_gps_sessions
         SET sync_status = ?,
             last_error_code = ?,
             last_error_message = ?,
             updated_at = ?
         WHERE sync_status = ?;`,
        [
            PHONE_GPS_RECORDING_SYNC_ERROR,
            'interrupted',
            'Upload interrupted before completion',
            Date.now(),
            PHONE_GPS_RECORDING_SYNC_UPLOADING,
        ]
    )
    const recoveredCount = getResultChanges(result)
    await persistIfChanged(recoveredCount)
    return { ok: true, recoveredCount }
}

const getPhoneGpsRecordingSummary = async () => {
    await ensurePhoneGpsRecordingSchema()

    const result = await dbQuery(
        `SELECT
            COUNT(*) as count,
            SUM(CASE WHEN sync_status = 'local' THEN 1 ELSE 0 END) as local_count,
            SUM(CASE WHEN sync_status = 'uploading' THEN 1 ELSE 0 END) as uploading_count,
            SUM(CASE WHEN sync_status = 'synced' THEN 1 ELSE 0 END) as synced_count,
            SUM(CASE WHEN sync_status = 'error' THEN 1 ELSE 0 END) as error_count,
            MIN(started_at) as oldest,
            MAX(started_at) as newest
         FROM phone_gps_sessions;`
    )
    const row = result?.values?.[0] || {}
    return {
        sessionsCount: Number(row.count) || 0,
        localCount: Number(row.local_count) || 0,
        uploadingCount: Number(row.uploading_count) || 0,
        syncedCount: Number(row.synced_count) || 0,
        errorCount: Number(row.error_count) || 0,
        oldestSessionAt: normalizePhoneGpsTimestampMs(row.oldest),
        newestSessionAt: normalizePhoneGpsTimestampMs(row.newest),
        now: Date.now(),
    }
}

export default defineSqliteFeature({
    key: 'phoneGpsRecording',
    ensureSchema: () => ensurePhoneGpsRecordingSchema(),
    onBoot: () => recoverInterruptedPhoneGpsUploads(),
    getSummary: () => getPhoneGpsRecordingSummary(),
    purge: async () => ({ ok: true, skipped: true }),
    fallbacks: {
        onBoot: { ok: false, recoveredCount: 0 },
        getSummary: null,
        purge: { ok: true, skipped: true },
    },
})
